import React, {useEffect, useState} from 'react';

import './Users.scss';
import { useQuery } from '@apollo/client';
import gqlSchemaLibrary from '../services/graphql.schemas';

import { Subject } from 'rxjs';
import ButtonIcon from '../forms/buttons/Button.icon';
import {useNavigate} from 'react-router-dom';
import cacheStorage from '../services/cacheStorage';
import UserBaseUser from './Users.user';
import SpinnerLoader from "../visuals/SpinnerLoader";
import UsersDropdownText from "../forms/dropdown/dropdown.users";
import {useTranslation} from "react-i18next";
import {PageViewLog} from "../services/interactions/page.view";
import AuditTrail, {PAGES} from "../services/interactions/audit.trail";


function UsersBase({ currentOrg, toaster, currentUser }) {
    const {t} = useTranslation(['users','common']);
     const [data, setData] = useState({ getUserClinicians: [] });
    const [loadData, setLoadData] = useState(true);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [sortAscending, setSortAscending] = useState(true);
    const [sorting, setSorting] = useState('name');
    const [newsOnTop, setNewsOnTop] = useState(true);

    const [searchFocused, changeSearchFocused] = useState(false);
    const [search, changeSearch] = useState('');

    const [roleFilter, setRoleFilter] = useState(null);
    const [userActive, setUserActive] = useState(true);

    const [searchTerm] = useState(new Subject());
    const navigate = useNavigate();

    useEffect(() => {
        if (!currentUser.userRoles.userClinicians.create) {
            navigate(-1);
        }
    }, [])

    useQuery(gqlSchemaLibrary.clinicians.queries.getCliniciansByOrg, {
        onCompleted: ({ getCliniciansByOrg }) => {
            const cliniciansByOrg = getCliniciansByOrg.map(p => ({ ...p }));
            console.log('[User List] User Clinicians: ', cliniciansByOrg);
            if (cliniciansByOrg) {
                const lastCliniciansSeenString = cacheStorage.getItem(`${currentUser.selectedOrgId}_clinicianlist`);
                if (lastCliniciansSeenString && lastCliniciansSeenString.value) {
                    const lastPatientsSeenId = JSON.parse(lastCliniciansSeenString.value);
                    cliniciansByOrg.forEach((clinician) => clinician.isNew = !lastPatientsSeenId.some(id => id === clinician._id));
                    // Set for program assign to show as "new", but avoid overwriting
                    const newForPrograms = cacheStorage.getItem(`${currentUser.selectedOrgId}_programPatients`);
                    if (!!newForPrograms && newForPrograms.value) {
                        const allNewForPrograms = JSON.parse(newForPrograms.value).concat(cliniciansByOrg.filter(p => p.isNew).map(p => p._id));
                        cacheStorage.saveItem(`${currentUser.selectedOrgId}_programPatients`, JSON.stringify(allNewForPrograms));
                    } else {
                        cacheStorage.saveItem(`${currentUser.selectedOrgId}_programPatients`, JSON.stringify(cliniciansByOrg.filter(p => p.isNew).map(p => p._id)));
                    }
                }
                setData({ getUserClinicians: cliniciansByOrg });
                onSelect(cliniciansByOrg[0] || null);
                cacheStorage.saveItem(`${currentUser.selectedOrgId}_clinicianlist`, JSON.stringify(cliniciansByOrg.map(p => p._id)));
            }
            setLoadData(false);
        },
        fetchPolicy: 'cache-and-network'
    });

    const onSelect = (clinician) => {
        setSelectedPatient(clinician);
    }


    const handleFocus = (bool) => {
        return () => {
            AuditTrail.sendLog(PAGES.USERS_LIST, {tag: 'input', event: 'onSearch', action: 'input search patients', focus: bool, value: search});
            changeSearchFocused(bool);
        }
    }

    const handleSearch = (e) => {
        const value = e.target.value;
        changeSearch(value)
        searchTerm.next(value);
    }

    const openRoleFilter = (program) => {
        setRoleFilter(program.value);
    }

    const changeUserActive = (userActive) => {
        AuditTrail.sendLog(PAGES.USERS_LIST, {tag: 'dropdown', event: 'onChange', action: 'dropdown filter list users', value: !userActive});
        setUserActive(!userActive);
    }

    const propagateUserChange = (user) => {
        const clinicians = data.getUserClinicians;
        const indexToUpdate = clinicians.findIndex(c => c.userId === user._id)
        if (indexToUpdate > -1) {
            const edittedClinician = clinicians[indexToUpdate];
            edittedClinician.user = Object.assign({}, edittedClinician.user, user);
            console.log('CLINICIANS PROPAGATE CHANGE: ', clinicians);
            setData({ getUserClinicians: clinicians });
        } else {
            console.log('! CLINICIANS PROPAGATE CHANGE: ', clinicians);
        }
    }

    const dropdownOptions = [{ label: t('all-roles'), value: null }, { label: t('administrator'), value: 'admin' }, { label: t('clinician'), value: 'clinician' }, { label: t('staff'), value: 'staff' }, { label: t('read-only'), value: 'read' }, { label: t('command-only'), value: 'command-only' }];
    const filteredPatients = data.getUserClinicians
        .filter(p => {
            if (!search) {
                return true;
            }
            const firstName = (p.user.bio.firstname || '').toLowerCase();
            const lastName = (p.user.bio.lastname || '').toLowerCase();
            const searchWords = search.split(' ').map(w => w.toLowerCase());
            return searchWords.reduce((p, word) => {
                return p && (firstName.includes(word) || lastName.includes(word));
            }, true);
        })
        .filter(p => {
            let show = !!roleFilter ? p.role === roleFilter : true;
            if (userActive) {
                return show && !p.user.isDisabled;
            } else {
                return show && p.user.isDisabled;
            }
        })
        .sort( (a, b) => {
            if(!sorting){
                return true;
            }
            if(sorting === 'name'){
                const aName = (a.user.bio.lastname||'')+' '+(a.user.bio.firstname||'');
                const bName = (b.user.bio.lastname||'')+' '+(b.user.bio.firstname||'');
                if(sortAscending){
                    return aName.localeCompare(bName);
                }else{
                    return bName.localeCompare(aName);
                }
            }else{
                const aRole = a.role;
                const bRole = b.role;
                if(sortAscending){
                    return aRole.localeCompare(bRole);
                }else{
                    return bRole.localeCompare(aRole);
                }
            }
        })

    const sortList = (key) => {
        return () => {
            AuditTrail.sendLog(PAGES.USERS_LIST, {tag: 'button', event: 'onClick', action: 'button toggle sort', sort: key, sort_ascending: !sortAscending});
            setSorting(key);
            setSortAscending(!sortAscending);
            setNewsOnTop(false);
        }
    }

    const oldFilteredPatients = filteredPatients.filter(p => {
        if(newsOnTop){
            return p && !p.isNew;
        }
        return true;
    });
    const newFilteredPatients = filteredPatients.filter(p => {
        if(newsOnTop){
            return p && p.isNew;
        }
        return false;
    });
    const countUsers = newFilteredPatients.length+oldFilteredPatients.length;

    const onCreateUser = () => {
        AuditTrail.sendLog(PAGES.USERS_LIST, {tag: 'button', event: 'onClick', action: 'button new user'});
        navigate('/users/create/');
    }

    return (
        <div id="UsersBase">
            <div className="filter">
                <UsersDropdownText
                    transparent={true}
                    onChange={openRoleFilter}
                    changeUserActive={changeUserActive}
                    options={dropdownOptions}
                    label={t('filter-view-by-role').toUpperCase()}
                    placeholder={t('filter-by-role')}
                    value={roleFilter}
                    userActive={userActive}
                    event={PAGES.USERS_LIST}
                />
                <div className={`search ${searchFocused ? 'focused' : ''}`}>
                    <input value={search} placeholder="Search..." onChange={handleSearch} onFocus={handleFocus(true)} onBlur={handleFocus(false)} />
                    <i className="material-icons">
                        search
                    </i>
                </div>
                <div className="addAction">
                    <p className="_counter">
                        {countUsers} user{countUsers === 1? '' : 's'}
                    </p>
                    {
                        !currentUser.userRoles.userClinicians.create ?
                            ''
                            :
                            <div className="enrollAction" onClick={onCreateUser}>
                                <ButtonIcon>
                                    <span className="material-icons">
                                        add
                                    </span>
                                </ButtonIcon>
                                <p>{t('common:new')}</p>
                            </div>
                    }
                </div>
            </div>
            <div className="patientsList scrollBar">
                <div className="headers">
                    <div></div>
                    <div className="selectable sortable">
                        <p onClick={sortList('name')}>
                            {t('name')}
                        </p>
                        <div className="_updown" onClick={sortList('name')}>
                            <span className="_up material-icons">expand_less</span>
                            <span className="_down material-icons">expand_more</span>
                        </div>
                    </div>
                    <div className="selectable">
                        <p>
                            {t('telephone')}
                        </p>
                        <div className="_updown"></div>
                    </div>
                    <div className="selectable sortable">
                        <p onClick={sortList('role')}>
                            {t('role')}
                        </p>
                        <div className="_updown" onClick={sortList('role')}>
                            <span className="_up material-icons">expand_less</span>
                            <span className="_down material-icons">expand_more</span>
                        </div>
                    </div>
                    <div className="selectable">
                        <p>
                            {t('date-of-birth')}
                        </p>
                        <div className="_updown"></div>
                    </div>

                    <div></div>
                </div>
                {
                    newFilteredPatients.map((clinician) => (
                        <UserBaseUser key={clinician._id} currentOrg={currentOrg} clinician={clinician} selected={selectedPatient && clinician._id === selectedPatient._id} onSelected={setSelectedPatient} propagateUserChange={propagateUserChange} />
                    ))
                }
                {
                    oldFilteredPatients.map((clinician) => (
                        <UserBaseUser key={clinician._id} currentOrg={currentOrg} clinician={clinician} selected={selectedPatient && clinician._id === selectedPatient._id} onSelected={setSelectedPatient} propagateUserChange={propagateUserChange} />
                    ))
                }
                {loadData ? <SpinnerLoader /> : ''}
            </div>
        </div>
    )
}

export default PageViewLog(UsersBase);
